import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { ReferralAnalyticsEvent, ReferralRouteParams } from '../types/';
import { EventListItem } from '../types/api.types';

import { IState } from 'models/reducers/i-state.interface';

import {
  getEventsListService,
  getReferralEventService,
  getUserMetricsService,
  getUserStatusService,
} from 'services/referral-event.service';

import { REFERRAL_CACHE_KEYS } from '../constants';
import { transformApiDataToReferralEventData } from '../helpers/transform-api-data.helper';
import { MOCK_REFERRAL_EVENT_DATA } from '../mocks/referral-event.mock';

// Toggle between mock data and API data
const USE_MOCK_DATA = false;

const fetchApiData = async <T>(
  serviceCall: (id: string, signal: AbortSignal) => Promise<{ data: T }>,
  eventId: string,
  signal: AbortSignal
): Promise<T> => {
  const response = await serviceCall(eventId, signal);
  return response.data;
};

const fetchReferralEventFromApi = (eventId: string, signal: AbortSignal) =>
  fetchApiData(getReferralEventService, eventId, signal);

const fetchUserMetricsFromApi = (eventId: string, signal: AbortSignal) =>
  fetchApiData(getUserMetricsService, eventId, signal);

const fetchUserStatusFromApi = (eventId: string, signal: AbortSignal) =>
  fetchApiData(getUserStatusService, eventId, signal);

const transformEventToAnalyticsEvent = (event: EventListItem): ReferralAnalyticsEvent => {
  const now = new Date().getTime();
  const startDate = new Date(event.start_datetime).getTime();
  const isUpcoming = startDate > now || event.status === 0 || event.status === 1;

  return {
    id: event.id,
    name: event.name,
    imageUrl: event.image_url,
    isVip: event.event_label === 'vip',
    isUpcoming,
    eventType: event.event_type,
  };
};

type UseReferralEventProps = {
  eventTypeFilter?: 'referral' | 'trading' | 'all';
  filterUpcomingOnly?: boolean;
  sortByStartDate?: 'asc' | 'desc';
  skipUserMetrics?: boolean;
};

export const useReferralEvent = (options: UseReferralEventProps = {}) => {
  const {
    eventTypeFilter = 'referral',
    filterUpcomingOnly = false,
    sortByStartDate,
    skipUserMetrics = false,
  } = options;
  const { id } = useParams<ReferralRouteParams>();
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const isQueryEnabled = !USE_MOCK_DATA && !!id;
  const isUserStatusEnabled = isQueryEnabled && !!walletAddress;
  const isUserMetricsEnabled = isQueryEnabled && !skipUserMetrics;

  const eventQuery = useQuery({
    queryKey: [REFERRAL_CACHE_KEYS.REFERRAL_EVENT, id],
    queryFn: ({ signal }) => fetchReferralEventFromApi(id!, signal!),
    enabled: isQueryEnabled,
    retry: false,
  });

  const metricsQuery = useQuery({
    queryKey: [REFERRAL_CACHE_KEYS.USER_METRICS, id],
    queryFn: ({ signal }) => fetchUserMetricsFromApi(id!, signal!),
    enabled: isUserMetricsEnabled,
    retry: false,
  });

  const userStatusQuery = useQuery({
    queryKey: [REFERRAL_CACHE_KEYS.USER_STATUS, id],
    queryFn: ({ signal }) => fetchUserStatusFromApi(id!, signal!),
    enabled: isUserStatusEnabled,
    retry: false,
  });

  const eventsListQuery = useQuery({
    queryKey: [REFERRAL_CACHE_KEYS.EVENTS_LIST, eventTypeFilter, filterUpcomingOnly, sortByStartDate],
    queryFn: ({ signal }) => getEventsListService(eventTypeFilter, signal!),
    enabled: !USE_MOCK_DATA,
    retry: false,
  });

  // Transform events list and filter out current event
  const analyticsEvents = useMemo(() => {
    if (USE_MOCK_DATA) {
      return MOCK_REFERRAL_EVENT_DATA.analyticsEvents;
    }

    if (!eventsListQuery.data?.data?.results) {
      return [];
    }

    let events = eventsListQuery.data.data.results;
    const currentEventId = id ? Number.parseInt(id, 10) : undefined;

    // Filter out current event if id is valid
    if (currentEventId && !Number.isNaN(currentEventId)) {
      events = events.filter((event: EventListItem) => event.id !== currentEventId);
    }

    // Filter by status if filterUpcomingOnly is enabled (status 0 or 1)
    if (filterUpcomingOnly) {
      events = events.filter((event: EventListItem) => event.status === 0 || event.status === 1);
    }

    // Sort by start_datetime if sortByStartDate is provided
    if (sortByStartDate) {
      events = [...events].sort((a: EventListItem, b: EventListItem) => {
        const dateA = new Date(a.start_datetime).getTime();
        const dateB = new Date(b.start_datetime).getTime();

        // Handle invalid dates - put them at the end
        if (Number.isNaN(dateA) && Number.isNaN(dateB)) return 0;
        if (Number.isNaN(dateA)) return 1;
        if (Number.isNaN(dateB)) return -1;

        return sortByStartDate === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    return events.map(transformEventToAnalyticsEvent);
  }, [eventsListQuery.data, id, filterUpcomingOnly, sortByStartDate]);

  // If using mock data, return mock data with loading state
  if (USE_MOCK_DATA) {
    return {
      data: MOCK_REFERRAL_EVENT_DATA,
      analyticsEvents: MOCK_REFERRAL_EVENT_DATA.analyticsEvents,
      isLoading: false,
      isError: false,
      error: null,
      eventData: undefined,
      userMetrics: undefined,
      userStatus: undefined,
      isUserMetricsError: false,
      userMetricsError: null,
    };
  }

  // Only event query error should block the whole page
  // Skip metricsQuery loading if skipUserMetrics is true
  const isLoading =
    eventQuery.isLoading ||
    (skipUserMetrics ? false : metricsQuery.isLoading) ||
    userStatusQuery.isLoading ||
    eventsListQuery.isLoading;
  const isError = eventQuery.isError;
  const error = eventQuery.error;

  // Transform event data with userMetrics if available
  const transformedData = eventQuery.data
    ? transformApiDataToReferralEventData(eventQuery.data, metricsQuery.data, USE_MOCK_DATA)
    : undefined;

  return {
    data: transformedData,
    analyticsEvents,
    isLoading,
    isError,
    error,
    eventData: eventQuery.data,
    userMetrics: metricsQuery.data,
    userStatus: userStatusQuery.data,
    isUserMetricsError: metricsQuery.isError,
    userMetricsError: metricsQuery.error,
  };
};
